import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import ImportOrders from './ImportOrders';

function OrdersPage({ token }) {
  const [activeTab, setActiveTab] = useState('EXPORT');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [form, setForm] = useState({
    customerCode: '', orderNo: '', orderDate: '',
    currency: 'USDINR', fcAmount: '', costRate: '',
    creditDays: '', expectedShipmentDate: '',
    advanceExpectedFc: '', advanceExpectedDate: '', notes: ''
  });

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/orders`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      customerCode: '', orderNo: '', orderDate: '',
      currency: 'USDINR', fcAmount: '', costRate: '',
      creditDays: '', expectedShipmentDate: '',
      advanceExpectedFc: '', advanceExpectedDate: '', notes: ''
    });
    setEditingOrderId(null);
    setShowForm(false);
  };

  const handleEdit = (order) => {
    setForm({
      customerCode: order.customerCode || '',
      orderNo: order.orderNo || '',
      orderDate: order.orderDate || '',
      currency: order.currency || 'USDINR',
      fcAmount: order.fcAmount || '',
      costRate: order.costRate || '',
      creditDays: order.creditDays || '',
      expectedShipmentDate: order.expectedShipmentDate || '',
      advanceExpectedFc: order.advanceExpectedFc || '',
      advanceExpectedDate: order.advanceExpectedDate || '',
      notes: typeof order.notes === 'string' ? order.notes : ''
    });
    setEditingOrderId(order.orderId);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        customerCode: form.customerCode,
        orderNo: form.orderNo,
        orderDate: form.orderDate,
        currency: form.currency,
        fcAmount: parseFloat(form.fcAmount),
        costRate: parseFloat(form.costRate),
        creditDays: parseInt(form.creditDays),
        expectedShipmentDate: form.expectedShipmentDate || null,
        advanceExpectedFc: form.advanceExpectedFc ? parseFloat(form.advanceExpectedFc) : null,
        advanceExpectedDate: form.advanceExpectedDate || null,
        notes: form.notes || null
      };

      if (editingOrderId) {
        await axios.put(`${CONFIG.API}/api/bills/orders/${editingOrderId}`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${CONFIG.API}/api/bills/orders`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
      }

      resetForm();
      loadOrders();
    } catch (err) {
      alert('Error saving order. Please check all fields.');
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await axios.put(`${CONFIG.API}/api/bills/orders/${orderId}/cancel`, {}, {
        headers: { authorization: `Bearer ${token}` }
      });
      loadOrders();
    } catch (err) {
      alert('Cannot cancel this order.');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'OPEN': 'badge-open',
      'PART_INVOICED': 'badge-partial',
      'FULLY_INVOICED': 'badge-done',
      'CANCELLED': 'badge-cancelled'
    };
    return map[status] || 'badge-open';
  };

  // Filter orders based on search bar
  const filteredOrders = orders.filter(order => {
    const matchStatus = filterStatus === '' || order.status === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      order.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  return (
    <div className="content">

      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px',
        borderBottom: '2px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('EXPORT')}
          style={{
            padding: '10px 24px', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: '600',
            background: activeTab === 'EXPORT' ? '#1F4E79' : '#f1f5f9',
            color: activeTab === 'EXPORT' ? '#fff' : '#64748b',
            borderRadius: '8px 8px 0 0', marginRight: '4px'
          }}>
          📤 Export Orders
        </button>
        <button
          onClick={() => setActiveTab('IMPORT')}
          style={{
            padding: '10px 24px', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: '600',
            background: activeTab === 'IMPORT' ? '#1F4E79' : '#f1f5f9',
            color: activeTab === 'IMPORT' ? '#fff' : '#64748b',
            borderRadius: '8px 8px 0 0'
          }}>
          📥 Import Orders
        </button>
      </div>

      {/* Import Orders Tab */}
      {activeTab === 'IMPORT' && (
        <ImportOrders token={token} />
      )}

      {/* Export Orders Tab */}
      {activeTab === 'EXPORT' && <>

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total orders</div>
          <div className="metric-value val-blue">{orders.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">
            {orders.filter(o => o.status === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Part invoiced</div>
          <div className="metric-value val-amber">
            {orders.filter(o => o.status === 'PART_INVOICED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Fully invoiced</div>
          <div className="metric-value val-blue">
            {orders.filter(o => o.status === 'FULLY_INVOICED').length}
          </div>
        </div>
      </div>

      {/* Header row */}
      <div className="section-header">
        <div className="section-title">
          {editingOrderId ? 'Edit order' : 'All orders'}
        </div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New order'}
        </button>
      </div>

      {/* Entry / Edit form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '720px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingOrderId ? '✏️ Edit order' : '📋 New order'}
          </div>

          {/* Row 1 — Customer + Order no */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Customer code</label>
              <input name="customerCode" value={form.customerCode}
                onChange={handleChange} placeholder="e.g. SMITH-UK" />
            </div>
            <div className="form-field">
              <label>Order no</label>
              <input name="orderNo" value={form.orderNo}
                onChange={handleChange} placeholder="Your reference" />
            </div>
          </div>

          {/* Row 2 — Date, Currency, FC Amount, Cost Rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Order date</label>
              <input type="date" name="orderDate" value={form.orderDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange}>
                <option value="USDINR">USD/INR</option>
                <option value="EURINR">EUR/INR</option>
                <option value="GBPINR">GBP/INR</option>
                <option value="AEDINR">AED/INR</option>
                <option value="SGDINR">SGD/INR</option>
                <option value="AUDINR">AUD/INR</option>
              </select>
            </div>
            <div className="form-field">
              <label>FC amount</label>
              <input type="number" name="fcAmount" value={form.fcAmount}
                onChange={handleChange} placeholder="0" />
            </div>
            <div className="form-field">
              <label>Cost rate</label>
              <input type="number" name="costRate" value={form.costRate}
                onChange={handleChange} placeholder="e.g. 84.50" />
            </div>
          </div>

          {/* Row 3 — Credit days, Shipment date, Adv FC, Adv date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Credit days</label>
              <input type="number" name="creditDays" value={form.creditDays}
                onChange={handleChange} placeholder="e.g. 60" />
            </div>
            <div className="form-field">
              <label>Shipment date</label>
              <input type="date" name="expectedShipmentDate"
                value={form.expectedShipmentDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Advance FC (opt)</label>
              <input type="number" name="advanceExpectedFc"
                value={form.advanceExpectedFc} onChange={handleChange}
                placeholder="Optional" />
            </div>
            <div className="form-field">
              <label>Advance date (opt)</label>
              <input type="date" name="advanceExpectedDate"
                value={form.advanceExpectedDate} onChange={handleChange} />
            </div>
          </div>

          {/* Row 4 — Notes full width */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #CBD5E1',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingOrderId ? '✓ Update order' : '✓ Save order'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_INVOICED">Part invoiced</option>
          <option value="FULLY_INVOICED">Fully invoiced</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredOrders.length} of {orders.length} orders
        </span>
      </div>

      {/* Orders table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredOrders.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No orders found. Try changing the filters.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Order no</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Currency</th>
                <th>FC amount</th>
                <th>Cost rate</th>
                <th>FC balance</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.orderId}>
                  <td>{order.orderNo}</td>
                  <td>{order.customerCode}</td>
                  <td>{order.orderDate}</td>
                  <td>{order.currency}</td>
                  <td>{Number(order.fcAmount).toLocaleString()}</td>
                  <td>{Number(order.costRate).toFixed(4)}</td>
                  <td>{Number(order.fcBalance).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="btn-secondary"
                      onClick={() => handleEdit(order)}
                      style={{ marginRight: '6px' }}>
                      Edit
                    </button>
                    {order.status === 'OPEN' && (
                      <button
                        className="btn-danger"
                        onClick={() => handleCancel(order.orderId)}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </>}
    </div>
  );
}

export default OrdersPage;